
import _ from 'underscore'
import $ from 'jquery'
import Backbone from 'backbone'
import MapModel from './models/MapModel'
import MapSelection from './selections/MapSelection'
import MapView from './views/MapView'
import UnitCollection from './collections/UnitCollection'

let AutoScale = {
  WIDTH: 'width',
  CLIENT_RECT: 'clientRect',
  NONE: 'none'
}

let AUTO_SCALE = [
  AutoScale.WIDTH,
  AutoScale.CLIENT_RECT,
  AutoScale.NONE,
]

class UnitMap {

  /**
   * UnitMap instance.
   * 
   * @param  {string|HTMLElement} element Must be the id of an element, or a
   *                                      DOM element reference.
   * @param  {Object} options
   * @return {Object} A map object
   */
  constructor(element, options = {}) {
    if (typeof(element) === 'string') {
      element = document.getElementById(element)
    }

    this.el = element
    this.options = options
    this.model = null
    this.view = null
    this.requestFrameId = null
    
    this.handleWindowResize = this.handleWindowResize.bind(this)
    this.handleAutoScale = this.handleAutoScale.bind(this)

    // Ensure `autoScale` option is known.
    
    if (options.autoScale && AUTO_SCALE.indexOf(options.autoScale) === -1) {
      throw new Error('The autoScale value of "' + options.autoScale + '" is unknown.')
      options.autoScale = null
    }

    this._autoScale = options.autoScale || AutoScale.CLIENT_RECT
    this._backgroundColor = options.backgroundColor
    this._panZoom = options.panZoom === false ? false : true
    this._expandableBy = options.expandableBy
    this._backgroundLevelId = options.backgroundLevelId
    this._maxScale = 5;

    if (window) {
      $(window).on('resize', this.handleWindowResize)
    }

    _.extend(this, Backbone.Events)
  }

  load(url, callback) {
    $.get(url, function (response) {
      this.document(response)
      this.trigger('ready', {map: this})

      if (callback) {
        callback(response)
      }
    }.bind(this), 'json')
  }

  select(selector) {
    return MapSelection.select(selector, this.model)
  }

  document(doc) {    
    if (this.model) {
      this.model = null
      this.view.remove()
      this.view = null
    }

    if (!_.isObject(doc)) {
      return
    }

    this.model = new MapModel(doc)
    this.model.set({
      backgroundColor: this._backgroundColor,
      panZoom: this._panZoom,
      width: doc.width,
      height: doc.height,
      originWidth: doc.width,
      originHeight: doc.height
    })

    this.handleAutoScale()
    this.initExpandableDocument();

    // Bind the Map instance to the `dispatcher` property of the MapModel
    // instance. Internal models and views will use this property to dispach
    // Public API events.
    
    this.model.dispatcher = this
    
    this.view = new MapView({model: this.model})
    this.el.appendChild(this.view.el)
    this.view.render()
  }

  initExpandableDocument() {
    this._expandable = true
    this._expanded = false
    let tag = this._expandableBy
    let levels = this.select('levels')
    let background = levels.find(this._backgroundLevelId)

    let query = {}
    query[tag] = true
    let result = levels.has(query)

    // Return if no levels are found for the given query.
    
    if (result.length === 0) {
      this._expandable = false
      return
    }

    // Create list of select queries based on unique values of the given tag.
    
    let groups = this._groups = _(
        result.data().map(data => data.tags[tag])
      )
      .uniq()
      .map(value => {
        let query = {}
        query[tag] = value
        return query 
      })

    if (background.length > 0) {
      _.each(groups, (group, idx) => {
        background.clone(`background.${idx}`, _.assign({_expand: true}, group)).hide()
      })
    }
  }

  translate(x, y) {
    if (x === undefined && y === undefined) {
      return [this.model.get('x'), this.model.get('y')]
    }

    this.model.set({x, y})
  }

  panZoom(enabled = true) {
    this._panZoom = enabled === false ? false : true
    this.model.set('panZoom', this._panZoom)
  }

  autoScale(value) {
    // Ensure value is a known.
    
    if (value && AUTO_SCALE.indexOf(value) === -1) {
      throw new Error('The autoScale value of "' + value + '" is unknown.')
      return this
    }

    this._autoScale = value
    this.handleAutoScale()
  }

  expand(options = {}) {
    if (!this._expandable || this._expanded) {
      return
    }

    options = _.defaults(options, {
      animate: false,
      duration: 550,
      ratio: 0.5,
      margin: 0,
      ease: 'cubic-in-out'
    });

    this._expOpts = options
    this._expanded = true

    let {
      ratio,
      margin,
      animate,
      duration,
      ease
    } = options

    let bounds = {
      width: this.model.get('viewportWidth'),
      height: this.model.get('viewportHeight'),
    }
    let size = {
      width: this.model.get('originWidth'),
      height: this.model.get('originHeight')
    }
    let scale = ratio
    size.width = scale * size.width,
    size.height = scale * size.height

    let levels = this.select('levels')
    let background = levels.find(this._backgroundLevelId)
    let groups = this._groups

    let tx = margin
    let ty = margin
    let w = 0
    let selection = null

    levels.has({_expand: true}).show()
    levels.find('background').hide()

    selection = levels.has({floor: true})
    
    animate 
      ? selection.transition({x:margin, y:margin, scale}, duration, ease)
      : selection.translate(margin, margin).scale(scale)

    _.each(groups, (group, idx) => {
      selection = levels.where(group)

      animate
        ? selection.transition({x:tx, y:ty}, duration, ease)
        : selection.translate(tx, ty)

      tx += size.width + margin
      if (tx > bounds.width) {
        w = tx
        tx = margin
        ty += size.height + margin
      }
    })

    size.width = w
    size.height = ty + size.height + margin

    if (size.width === 0 && size.height === 0) {
      size.width = this.model.get('originWidth'),
      size.height = this.model.get('originHeight')
    }

    let minScale = scaleToFit(size, bounds)
    scale = bounds.width / size.width

    let translate = [0, 0]
    let attr = {x: translate[0], y: translate[1], scale}
    
    this.model.set('scaleExtent', [minScale, this._maxScale])

    animate
      ? this.model.transition(attr, duration, ease)
      : this.model.set(attr)

    if (this._animateDelay) {
      clearTimeout(this._animateDelay)
      this._animateDelay = null
    }
  }

  collapse() {
    if (!this._expandable || !this._expanded) {
      return
    }

    let {
      margin,
      animate,
      duration,
      ease
    } = this._expOpts

    this._expanded = false

    let levels = this.select('levels')
    let selection = null

    selection = levels.has({floor: true})
    animate
      ? selection.transition({x:0, y:0, scale:1}, duration, ease)
      : selection.translate(0, 0).scale(1)

    if (animate) {
      this._animateDelay = setTimeout(function () {
        levels.has({_expand: true}).hide()
        levels.find('background').show()
      }, duration)
    }

    let bounds = {
      width: this.model.get('viewportWidth'),
      height: this.model.get('viewportHeight'),
    }
    let size = {
      width: this.model.get('originWidth'),
      height: this.model.get('originHeight')
    }
    let scale = scaleToFit(size, bounds)
    let translate = center(size, bounds, scale)
    let attr = {x: translate[0], y: translate[1], scale}
    
    this.model.set('scaleExtent', [scale, this._maxScale])

    animate
      ? this.model.transition(attr, duration, ease)
      : this.model.set(attr)
  }

  handleAutoScale() {
    let clientRect = this.el.getBoundingClientRect()
    let bounds = {
      width: clientRect.width,
      height: clientRect.height,
    }
    let size = {
      width: this.model.get('width'),
      height: this.model.get('height')
    }

    // If expanded, apply different scaling logic.
    
    if (this._expanded) {
      let {ratio, margin} = this._expOpts

      let scale = ratio
      let levels = this.select('levels')

      size.width = scale * this.model.get('originWidth'),
      size.height = scale * this.model.get('originHeight')
      
      let tx = margin
      let ty = margin
      let w = 0

      _.each(this._groups, (group, idx) => {
       levels.where(group).translate(tx, ty)

        tx += size.width + margin
        if (tx > bounds.width) {
          w = tx
          tx = margin
          ty += size.height + margin
        }
      })

      size.width = w
      size.height = ty + size.height + margin
    }

    // If autoScale is `none`, exit.

    if (this._autoScale === AutoScale.NONE) {
      this.model.set({
        viewportWidth: bounds.width,
        viewportHeight: bounds.height,
      })

      return
    }

    // If autoScale is set to `width`, the height is always derived from the
    // origin document width. This logic represents the same behavior as how an
    // image scales when no height is provided on an image tag.

    if (this._autoScale === AutoScale.WIDTH) {
      bounds.height = (size.height / size.width) * bounds.width
    }

    let minScale = scaleToFit(size, bounds)
    let scale, translate

    if (this._expanded) {
      scale = bounds.width / size.width
      translate = [0, 0]
    } else {
      scale = minScale
      translate = center(size, bounds, scale)
    }

    this.model.set({
      x: translate[0],
      y: translate[1],
      scale: scale,
      scaleExtent: [minScale, this._maxScale],
      viewportWidth: bounds.width,
      viewportHeight: bounds.height,
    })
  }

  handleWindowResize() {
    if (window.requestAnimationFrame) {
      cancelAnimationFrame(this.requestFrameId)
      this.requestFrameId = requestAnimationFrame(this.handleAutoScale)
    } else {
      this.handleAutoScale()
    }
  }
}

function center(size, bounds, scale = 1) {
  return [
    (bounds.width - size.width * scale) * 0.5,
    (bounds.height - size.height * scale) * 0.5,
  ]
}

function scaleToFit(size, bounds) {
  let scale = bounds.width / size.width

  if (size.height * scale > bounds.height) {
    scale = bounds.height / size.height
  }

  return scale
}

UnitMap.AutoScale = AutoScale
UnitMap.AUTO_SCALE = AUTO_SCALE

export default UnitMap
